/***
 *
 *   SCORING SUMMARY
 *   Compact display of a stored scoring result (response.scoring /
 *   risk_assessment.scoring summary object) plus the reviewer
 *   override control for authenticated surfaces.
 *
 *   Summary shape: { mode, scoring_version, score, rating,
 *   threshold_medium, threshold_high, breakdown: [{question,
 *   question_title, choice, score}], computed_at, override?: {rating,
 *   reason, user_id, created_at} }
 *   (question_title is absent on legacy summaries — fall back to question.)
 *
 **********/

import React, { useContext, useState } from "react";
import Axios from "axios";
import { Badge, Button, useAPI, ViewContext } from "components/lib";

const TIERS = ["low", "medium", "high"];
const TIER_COLORS = { low: "green", medium: "orange", high: "red" };

function capitalize(value) {
  if (typeof value !== "string" || !value) return "Unknown";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function RatingBadge({ label, rating }) {
  return (
    <span className="inline-flex items-center text-sm text-slate-700">
      {label && <span className="font-medium">{label}:</span>}
      <Badge
        text={capitalize(rating)}
        color={TIER_COLORS[rating] || "blue"}
      />
    </span>
  );
}

export function ScoringSummary({ scoring, rating }) {
  const summary = scoring && typeof scoring === "object" ? scoring : null;

  if (!summary && !rating) return null;

  const override =
    summary?.override && TIERS.includes(summary.override.rating)
      ? summary.override
      : null;
  const baseRating = summary?.rating || rating || null;
  const isCalculated = summary?.mode === "calculated";
  const breakdown = Array.isArray(summary?.breakdown) ? summary.breakdown : [];

  return (
    <div className="space-y-3 rounded-panel border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-slate-900">Risk Scoring</p>
        {summary && (
          <span className="text-xs uppercase tracking-wide text-slate-500">
            {isCalculated ? "Calculated" : "Manual"}
          </span>
        )}
      </div>

      {override ? (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-3">
            {baseRating && (
              <RatingBadge
                label={isCalculated ? "Calculated" : "Manual"}
                rating={baseRating}
              />
            )}
            <RatingBadge label="Override" rating={override.rating} />
          </div>
          <p className="text-sm text-slate-600">
            Override reason: {override.reason || "No reason recorded"}
          </p>
          {override.created_at && (
            <p className="text-xs text-slate-500">
              Overridden {new Date(override.created_at).toLocaleString()}
            </p>
          )}
        </div>
      ) : baseRating ? (
        <RatingBadge label="Rating" rating={baseRating} />
      ) : (
        <p className="text-sm text-slate-600">No rating recorded.</p>
      )}

      {isCalculated && (
        <p className="text-sm text-slate-700">
          Score: <strong>{Number(summary.score) || 0}</strong>{" "}
          <span className="text-slate-500">
            (medium ≥ {summary.threshold_medium}, high ≥{" "}
            {summary.threshold_high})
          </span>
        </p>
      )}

      {breakdown.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-slate-700">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-1 pr-2 font-medium">Question</th>
                <th className="py-1 pr-2 font-medium">Answer</th>
                <th className="py-1 text-right font-medium">Score</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((row, index) => (
                <tr
                  key={`${row?.question || "row"}-${index}`}
                  className="border-t border-slate-200"
                >
                  <td className="py-1 pr-2">
                    {String(row?.question_title ?? row?.question ?? "")}
                  </td>
                  <td className="py-1 pr-2">{String(row?.choice ?? "")}</td>
                  <td className="py-1 text-right">
                    {Number(row?.score) || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {summary?.computed_at && (
        <p className="text-xs text-slate-500">
          Computed {new Date(summary.computed_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}

/***
 *
 *   ScoreOverrideControl
 *   Reviewer rating override for calculated scoring summaries.
 *   Rendered only on authenticated surfaces; shown when the account's
 *   scoring_settings integration is enabled and the summary mode is
 *   calculated.
 *   UI gating is convenience only — the server enforces access.
 *
 *   POST /api/response/:id/score-override { rating, reason }
 *   (rating null clears an existing override; reason always required)
 *
 **********/

export function ScoreOverrideControl({ responseId, scoring, onUpdated }) {
  const context = useContext(ViewContext);
  const scoringApi = useAPI("/api/integration/type/scoring_settings");
  const [rating, setRating] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // scoring policy is its own integration type; `enabled` is the override toggle
  const scoringRow = Array.isArray(scoringApi?.data) ? scoringApi.data[0] : null;
  const overrideEnabled = scoringRow?.enabled === true;

  if (!responseId) return null;
  if (!scoring || scoring.mode !== "calculated") return null;
  if (!overrideEnabled) return null;

  const hasOverride = !!(
    scoring.override && TIERS.includes(scoring.override.rating)
  );

  const submit = async (nextRating) => {
    if (submitting) return;

    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setFormError("A reason is required.");
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      const res = await Axios.post(
        `/api/response/${responseId}/score-override`,
        { rating: nextRating, reason: trimmedReason }
      );

      setRating("");
      setReason("");

      if (typeof onUpdated === "function") {
        onUpdated(res?.data?.data || {});
      }

      context?.notification?.show(
        nextRating === null ? "Override cleared." : "Rating override saved.",
        "success",
        true
      );
    } catch (err) {
      setFormError(
        err?.response?.data?.message ||
          "Unable to save the rating override right now."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 rounded-panel border border-slate-200 bg-white p-4">
      <p className="text-sm font-semibold text-slate-900">Rating override</p>

      <label className="block text-sm text-slate-700">
        <span className="mb-1 block font-medium">Override rating</span>
        <select
          value={rating}
          onChange={(event) => setRating(event.target.value)}
          className="w-full max-w-xs rounded border border-slate-300 bg-white px-3 py-2 text-sm"
        >
          <option value="">Select rating…</option>
          {TIERS.map((tier) => (
            <option key={tier} value={tier}>
              {capitalize(tier)}
            </option>
          ))}
        </select>
      </label>

      <label className="block text-sm text-slate-700">
        <span className="mb-1 block font-medium">Reason (required)</span>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
          className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder={
            hasOverride
              ? "Why is this override being changed or cleared?"
              : "Why is this rating being overridden?"
          }
        />
      </label>

      {formError && <p className="text-sm text-sg-red">{formError}</p>}

      <div className="flex flex-wrap gap-3">
        <Button
          text={submitting ? "Saving..." : "Apply Override"}
          action={() => {
            if (!rating) {
              setFormError("Select a rating to apply.");
              return;
            }
            submit(rating);
          }}
        />
        {hasOverride && (
          <Button
            text={submitting ? "Saving..." : "Clear Override"}
            outline
            action={() => submit(null)}
          />
        )}
      </div>
    </div>
  );
}
